import {styled, useTheme} from '@mui/material/styles';
import {NavLink, useLocation} from 'react-router-dom';
import {Box, List, ListItemIcon, ListItemText, ListItemButton, IconButton, Typography} from "@mui/material";
import MuiDrawer from '@mui/material/Drawer';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import SettingsIcon from '@mui/icons-material/Settings';
import CustomTooltip from '../ui/CustomTooltip';
import GroupsIcon from '@mui/icons-material/Groups';
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import CustomTypography from "../ui/CustomTypography";
import BookingIcon from "../../../assets/icons/booking-white.png";
import ContainerIcon from "../../../assets/icons/containers-white.png";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { permissions, profile_types } from '../common/variables/static_variables';
import { RBACMenuGuard, useRBACStore } from '../../../plugins/RBACPlugin';
// import EmojiTransportationIcon from '@mui/icons-material/EmojiTransportation';
// import FenceIcon from '@mui/icons-material/Fence';
// import DevicesIcon from '@mui/icons-material/Devices';
import EmojiTransportationIcon from "../../../assets/icons/port.png";
import FenceIcon from "../../../assets/icons/gate.png";
import DevicesIcon from "../../../assets/icons/device.png";
import GroupIcon from '@mui/icons-material/Group';
import { useGuard } from "../../../plugins/RBACPlugin";
import DriveEtaIcon from '@mui/icons-material/DriveEta';

const drawerWidth = 240.5;

const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
    backgroundColor: theme.palette.primary.main,
})
  
const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
    backgroundColor: theme.palette.primary.main,
})

const DrawerHeader = styled('div')(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({theme}) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        variants: [
            {
                props: ({ open }) => open,
                style: {
                    ...openedMixin(theme),
                    '& .MuiDrawer-paper': openedMixin(theme),
                },
            },
            {
                props: ({ open }) => !open,
                style: {
                    ...closedMixin(theme),
                    '& .MuiDrawer-paper': closedMixin(theme),
                },
            },
        ],
    }),
)


const Sidebar = (props) => {
    const {open, handleDrawerClose} = props;

    const theme = useTheme();
    const location = useLocation();
    const {isAllowedUserTypes,} = useGuard();
    const { userType} = useRBACStore();


    const routes = [
        {
            section: "",
            items: [
                {text: "Dashboard", path: "/dashboard", icon: <DashboardIcon sx={{fontSize: 18}} />, 
                    allowedProfileTypes: [
                        { profileType: profile_types.PORT_EMPLOYEE, roles: ['Admin', 'Employee', 'GateAttendant', 'PortDriver', 'Staff'] },
                        { profileType: profile_types.BUSINESS, roles: ['Owner', 'Employee', 'BusinessOwner', 'BusinessDriver', 'BusinessEmployee'] },
                        { profileType: profile_types.INDIVIDUAL, roles: ['Driver', 'Resident'] },
                    ],
                    // allowedProfileTypes: [profile_types.PORT_EMPLOYEE, profile_types.BUSINESS, profile_types.INDIVIDUAL]
                },
                {text: "Organization", path: "/organization", icon: <GroupsIcon sx={{fontSize: 18}} />, 
                    allowedProfileTypes: [
                        { profileType: profile_types.PORT_EMPLOYEE, roles: ['Admin'] },
                    ],
                    // allowedProfileTypes: [profile_types.PORT_EMPLOYEE]
                },
            ],
        },
        {
            section: "Assets",
            items: [
                {text: "Vehicles", path: "/vehicles", icon: <AirportShuttleIcon sx={{fontSize: 18}} />, 
                    allowedProfileTypes: [
                        { profileType: profile_types.PORT_EMPLOYEE, roles: ['Admin', 'Employee', 'PortDriver', 'Staff'] },
                        { profileType: profile_types.BUSINESS, roles: ['Owner', 'Employee', 'BusinessOwner', 'BusinessDriver', 'BusinessEmployee', 'Employee', 'GateAttendant', 'PortDriver', 'Staff'] },
                        { profileType: profile_types.INDIVIDUAL, roles: ['Driver', 'Resident'] },
                    ],
                    // allowedProfileTypes: [profile_types.PORT_EMPLOYEE, profile_types.BUSINESS, profile_types.INDIVIDUAL]
                },
                {text: "Gate Pass", path: "/gatepass", icon: <ConfirmationNumberIcon sx={{fontSize: 18}} />, 
                    allowedProfileTypes: [
                        { profileType: profile_types.PORT_EMPLOYEE, roles: ['Admin', 'Employee', 'Employee', 'PortDriver', 'Staff'] },
                        { profileType: profile_types.BUSINESS, roles: ['Owner', 'Employee', 'BusinessOwner', 'BusinessDriver', 'BusinessEmployee'] },
                        { profileType: profile_types.INDIVIDUAL, roles: ['Driver', 'Resident'] },
                    ],
                    // allowedProfileTypes: [profile_types.PORT_EMPLOYEE, profile_types.BUSINESS, profile_types.INDIVIDUAL]
                },
                {text: "Bookings", path: "/bookings", icon: <img alt="" src={BookingIcon} style={{height: 19, width: 19}} />,
                    allowedProfileTypes: [
                        { profileType: profile_types.PORT_EMPLOYEE, roles: ['Admin', 'Employee', 'Employee', 'PortDriver', 'Staff'] },
                        { profileType: profile_types.BUSINESS, roles: ['Owner', 'Employee', 'BusinessOwner', 'BusinessDriver', 'BusinessEmployee'] },
                        { profileType: profile_types.INDIVIDUAL, roles: ['Driver', 'Resident'] },
                    ],
                    // allowedProfileTypes: [profile_types.PORT_EMPLOYEE, profile_types.BUSINESS, profile_types.INDIVIDUAL]
                },
                {text: "Containers", path: "/containers", icon: <img alt="" src={ContainerIcon} style={{height: 18, width: 18}} />, 
                    allowedProfileTypes: [
                        { profileType: profile_types.PORT_EMPLOYEE, roles: ['Admin', 'Employee', 'BusinessOwner', 'PortDriver', 'Staff'] },
                        { profileType: profile_types.BUSINESS, roles: ['Owner', 'Employee', 'BusinessOwner', 'BusinessDriver', 'BusinessEmployee'] },
                        { profileType: profile_types.INDIVIDUAL, roles: ['Driver', 'Resident'] },
                    ],
                    // allowedProfileTypes: [profile_types.PORT_EMPLOYEE, profile_types.BUSINESS]
                },
            ],
        },
        {
            section: "Management",
            items: [
                {text: "Users", path: "/user", icon: <GroupIcon sx={{fontSize: 18}} />, 
                    allowedProfileTypes: [
                        { profileType: profile_types.PORT_EMPLOYEE, roles: ['Admin', 'Employee', 'PortDriver', 'Staff'] },
                        { profileType: profile_types.BUSINESS, roles: ['Owner', 'Employee', 'BusinessOwner', 'BusinessEmployee'] },
                    ],
                    // allowedProfileTypes: [profile_types.PORT_EMPLOYEE, profile_types.BUSINESS]
                },
                {text: "Drivers", path: "/driver", icon: <DriveEtaIcon sx={{fontSize: 18}} />, 
                    allowedProfileTypes: [
                        { profileType: profile_types.PORT_EMPLOYEE, roles: ['Admin', 'Employee', 'PortDriver', 'Staff'] },
                        { profileType: profile_types.BUSINESS, roles: ['Owner', 'Employee', 'BusinessOwner', 'BusinessDriver', 'BusinessEmployee'] },
                    ],
                    // allowedProfileTypes: [profile_types.PORT_EMPLOYEE, profile_types.BUSINESS]
                },
            ],
        },
        {
            section: "Settings",
            // allowedProfileTypes: [profile_types.PORT_EMPLOYEE],
            items: [
                {
                    text: "Ports",
                    path: "/ports",
                    icon: <img alt="" src={EmojiTransportationIcon} style={{ height: 17, width: 18 }} />,
                    allowedProfileTypes: [
                        { profileType: profile_types.PORT_EMPLOYEE, roles: ['Admin', 'Employee', 'PortDriver', 'Staff'] },
                    ],
                    // allowedProfileTypes: [profile_types.PORT_EMPLOYEE],
                },
                {
                    text: "Gates",
                    path: "/gates",
                    icon: <img alt="" src={FenceIcon} style={{ height: 19, width: 19 }} />,
                    allowedProfileTypes: [
                        { profileType: profile_types.PORT_EMPLOYEE, roles: ['Admin', 'Employee', 'PortDriver', 'Staff'] },
                    ],
                    // allowedProfileTypes: [profile_types.PORT_EMPLOYEE],
                },
                {
                    text: "RBAC",
                    path: "/rbac",
                    icon: <SettingsIcon sx={{ fontSize: 18 }} />,
                    allowedProfileTypes: [
                        { profileType: profile_types.PORT_EMPLOYEE, roles: ['Admin'] },
                    ],
                    // allowedProfileTypes: [profile_types.PORT_EMPLOYEE],
                },
                {
                    text: "Device Management",
                    path: "/device-management",
                    icon: <img alt="" src={DevicesIcon} style={{ height: 19, width: 19 }} />,
                    allowedProfileTypes: [
                        { profileType: profile_types.PORT_EMPLOYEE, roles: ['Admin', 'Employee', 'PortDriver', 'Staff'] },
                    ],
                    // allowedProfileTypes: [profile_types.PORT_EMPLOYEE],
                },
            ],
        }

    ].filter((item) => {
        let rbac_menus = item.items.filter((menu) =>
            isAllowedUserTypes(menu.allowedProfileTypes)
        );
        item.items = rbac_menus;
        return rbac_menus.length > 0;
    });

    return (
        <Drawer variant="permanent" open={open}>
            <DrawerHeader sx={{bgcolor: "#fff", p: "0px 16px"}}>
                <CustomTypography 
                    variant="h5"
                    text={"SBMA"}
                    customStyles={{
                        marginBottom: 0, 
                        fontWeight: 600, 
                        fontSize: 18,
                    }}
                />

                <IconButton onClick={handleDrawerClose} sx={{ml: "auto"}}>
                    {theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
            </DrawerHeader>

            <List sx={{p: 2}}>
                {routes?.map((section, index) => (
                    <div key={index}>
                        {section.section && (
                            <Typography sx={{mb: "6px", mt: (section.section === "") ? 0 : 4, fontSize: 12, color: "#B6E1F2"}}>
                                {section.section}
                            </Typography>
                        )}

                        {section.items.map((item) => (
                            <RBACMenuGuard allowedUserTypes={item.allowedProfileTypes} requiredPermission={item.requiredPermission}>
                                <CustomTooltip placement="right" title={item.text}>
                                    <Box>
                                        <ListItemButton
                                            key={item.text}
                                            component={NavLink}
                                            to={item.path}
                                            selected={(() => {
                                                const currentRoot = location.pathname.split("/")[1];
                                                const itemRoot = item.path.split("?")[0].split("/")[1];

                                                return currentRoot === itemRoot;
                                            })()}
                                            sx={{
                                                p: "4px 10px",
                                                minWidth: "80px",
                                                borderRadius: "10px",
                                                '&.Mui-selected': {
                                                    bgcolor: theme.palette.primary.dark,
                                                    fontWeight: 700,
                                                    '&:hover': {bgcolor: theme.palette.primary.dark},
                                                },
                                                '&:hover': {
                                                    //
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{color: theme.palette.primary.contrastText, minWidth: "32px"}}>
                                                {item.icon}
                                            </ListItemIcon>

                                            <ListItemText 
                                                slotProps={{primary: {sx: {fontSize: "14px", color: theme.palette.primary.contrastText, fontWeight: (location.pathname === item.path) ? 600 : 400}}}} 
                                                primary={item.text} 
                                            />
                                        </ListItemButton>
                                    </Box>
                                </CustomTooltip>
                            </RBACMenuGuard>
                        ))}
                    </div>
                ))}
            </List>
        </Drawer>
    )
}

export default Sidebar